import { NgZone } from "@angular/core";
import { MarkerClusterer, Renderer, Cluster, ClusterStats, SuperClusterAlgorithm } from "@googlemaps/markerclusterer";

/** `className` is what draws the label as a rounded box; @types/googlemaps@3.39.12 predates it. */
type MarkerLabel = google.maps.MarkerLabel & { className?: string };

/** One pin: a coordinate, and the comparables recorded at it. */
export interface MarkerLayerPin<T> {
    /** Stable across reloads, so a marker that has not moved is not recreated. */
    key: string;
    /** The pin's label -- refNums of everything at this coordinate, '/'-joined. */
    title: string;
    latitude: number;
    longitude: number;
    items: T[];
}

const CLUSTER_IMAGE_PATH = 'https://raw.githubusercontent.com/googlemaps/v3-utility-library/master/markerclustererplus/images/m';
/** The sizes the classic cluster images are drawn at (`MarkerClusterer.IMAGE_SIZES`). */
const CLUSTER_IMAGE_SIZES = [53, 56, 66, 78, 90];

export interface MarkerLayerOptions<T> {
    /** The pin image. */
    iconUrl: string;
    /**
     * CSS class on the label, which is what draws it as a rounded box.
     *
     * `isStack` is true when the pin stands for more than one comparable -- the label is a
     * count then, not a refNum -- so a map that colours its pins by sub-type or by whether
     * the comparable is picked can answer for both cases.
     */
    labelClassName: (pin: MarkerLayerPin<T>, isStack: boolean) => string;
}

/**
 * Draws the comparable markers for a map, and clusters them.
 *
 * This is deliberately imperative rather than a template full of `<agm-marker>`: AGM builds
 * an Angular component *and* a directive for every pin, then re-diffs the lot whenever the
 * list changes, which is most of what a few hundred comparables cost. Here a pin is one
 * `google.maps.Marker`, created outside Angular, reused across reloads while its coordinate
 * is unchanged, and only re-entering the zone when the valuer actually clicks one.
 *
 * Clustering is `@googlemaps/markerclusterer`, whose supercluster index does not care how
 * many markers it is given -- unlike the archived markerclustererplus grid the other maps
 * still use. The cluster icons are the same images, so the maps look alike.
 */
export class ComparableMarkerLayer<T> {
    private _markers = new Map<string, google.maps.Marker>();
    private _clusterer: MarkerClusterer;

    constructor(
        map: google.maps.Map,
        private _zone: NgZone,
        private _options: MarkerLayerOptions<T>,
        private _onClick: (items: T[]) => void
    ) {
        this._zone.runOutsideAngular(() => {
            this._clusterer = new MarkerClusterer({
                map,
                markers: [],
                // Matched to the `gridSize`/`maxZoom` the other comparable maps cluster at,
                // so the two generations behave the same on screen.
                algorithm: new SuperClusterAlgorithm({ radius: 60, maxZoom: 15 }),
                renderer: this._renderer()
            });
        });
    }

    /**
     * Replaces what is on the map with `pins`. Markers whose key is unchanged are kept as
     * they are -- a pan that returns most of the same comparables redraws almost nothing.
     */
    setPins(pins: MarkerLayerPin<T>[]): void {
        this._zone.runOutsideAngular(() => {
            const next = new Map<string, google.maps.Marker>();
            const added: google.maps.Marker[] = [];

            pins.forEach(pin => {
                const existing = this._markers.get(pin.key);
                if (existing) {
                    // Same coordinate, but the comparables at it -- and so the label -- may
                    // have changed under a new filter.
                    existing.setLabel(this._label(pin));
                    existing.setTitle(this._tooltip(pin));
                    google.maps.event.clearListeners(existing, 'click');
                    existing.addListener('click', () => this._zone.run(() => this._onClick(pin.items)));
                    next.set(pin.key, existing);
                    this._markers.delete(pin.key);
                    return;
                }

                const marker = new google.maps.Marker({
                    position: { lat: pin.latitude, lng: pin.longitude },
                    label: this._label(pin),
                    title: this._tooltip(pin),
                    icon: { url: this._options.iconUrl }
                });
                marker.addListener('click', () => this._zone.run(() => this._onClick(pin.items)));
                next.set(pin.key, marker);
                added.push(marker);
            });

            // Whatever is left in _markers is no longer in the result set.
            const removed = Array.from(this._markers.values());
            removed.forEach(marker => google.maps.event.clearInstanceListeners(marker));
            if (removed.length > 0) {
                this._clusterer.removeMarkers(removed, true);
            }
            if (added.length > 0) {
                this._clusterer.addMarkers(added, true);
            }
            if (removed.length > 0 || added.length > 0) {
                this._clusterer.render();
            }

            this._markers = next;
        });
    }

    destroy(): void {
        this._markers.forEach(marker => google.maps.event.clearInstanceListeners(marker));
        this._markers.clear();
        this._clusterer.clearMarkers();
        this._clusterer.setMap(null);
    }

    /**
     * A pin standing for one comparable is labelled with its refNum. A pin standing for
     * several is labelled with how many.
     *
     * Joining the refNums instead -- 'DQ3/EQ1/FQ7/...' -- is what this replaces: the string
     * had no upper bound, so a deep stack drew a label wider than the streets around it, and
     * naming only the first would give it a primacy the data does not have. The refNums are
     * on the tooltip, and the click opens all of them.
     */
    private _label(pin: MarkerLayerPin<T>): MarkerLabel {
        const isStack = pin.items.length > 1;
        return {
            color: isStack ? '#ffffff' : '#000000',
            fontFamily: '',
            fontSize: '14px',
            fontWeight: 'bold',
            className: this._options.labelClassName(pin, isStack),
            text: isStack ? String(pin.items.length) : pin.title
        };
    }

    /** Every refNum at this coordinate, for the browser's own tooltip. */
    private _tooltip(pin: MarkerLayerPin<T>): string {
        return pin.title.split('/').join(' / ');
    }

    /**
     * The classic cluster images, chosen the way `madCalculator` chooses them: by the digit
     * count of the total. The number shown counts comparables rather than pins, since one
     * pin can stand for several sharing a coordinate.
     */
    private _renderer(): Renderer {
        return {
            render: (cluster: Cluster, _stats: ClusterStats, map: google.maps.Map) => {
                const total = cluster.markers.reduce((sum, marker) => {
                    const label = (marker as google.maps.Marker).getLabel();
                    return sum + (label && label.text ? label.text.split('/').length : 1);
                }, 0);

                const index = Math.min(String(cluster.count).length, CLUSTER_IMAGE_SIZES.length);
                const size = CLUSTER_IMAGE_SIZES[index - 1];

                return new google.maps.Marker({
                    position: cluster.position,
                    map,
                    icon: {
                        url: `${CLUSTER_IMAGE_PATH}${index}.png`,
                        scaledSize: new google.maps.Size(size, size)
                    },
                    label: {
                        text: String(total),
                        color: '#000000',
                        fontSize: '11px',
                        fontFamily: 'Arial,sans-serif',
                        fontWeight: 'bold'
                    },
                    // Above the pins, below anything the map draws on top.
                    zIndex: Number(google.maps.Marker.MAX_ZINDEX) + cluster.count
                });
            }
        };
    }
}
